import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { conversationsApi, messagesApi, storageApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { messageQueue } from '../state/messageQueue';
import type {
  ConversationResponse,
  MessageResponse,
  MessageCreate,
  AttachmentInput,
} from '../types';

function inferContentType(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
    webp: 'image/webp', heic: 'image/heic', heif: 'image/heif',
    mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
    avi: 'video/x-msvideo', mkv: 'video/x-matroska', m4v: 'video/mp4',
  };
  return map[ext] || 'application/octet-stream';
}

export function useConversation(conversationId: number | undefined) {
  const { data, error, isLoading, mutate } = useSWR<ConversationResponse>(
    conversationId ? `conversations/${conversationId}` : null,
    () => conversationsApi.get(conversationId!),
  );
  return { conversation: data, error, isLoading, mutate };
}

export function useConversations(search?: string) {
  const { user } = useAuth();
  // Send the active shop_id explicitly so:
  // - The URL surfaces what's being filtered (was implicit-from-JWT).
  // - Switching shops via authApi.switchShop refetches with the new id
  //   (cache key includes shop_id → SWR invalidates automatically).
  // - The backend ?shop_id= override path is exercised end-to-end.
  const shopId = user?.shop_id ?? undefined;
  const cacheKey = shopId
    ? `conversations?shop_id=${shopId}${search ? `&search=${encodeURIComponent(search)}` : ''}`
    : (search ? `conversations?search=${encodeURIComponent(search)}` : 'conversations');
  const { data, error, isLoading, mutate } = useSWR(
    cacheKey,
    () => conversationsApi.list({ limit: 100, search: search || undefined, shop_id: shopId }),
    { refreshInterval: 30000 },
  );

  const createConversation = async (): Promise<number> => {
    const result = await conversationsApi.create();
    await mutate();
    return result.id;
  };

  const deleteConversation = async (conversationId: number) => {
    await conversationsApi.delete(conversationId);
    await mutate((current) => current
      ? { ...current, conversations: current.conversations.filter((c) => c.id !== conversationId) }
      : current,
      { revalidate: false }
    );
  };

  const updateConversationStatus = async (conversationId: number, status: string) => {
    await conversationsApi.patch(conversationId, { status });
    await mutate((current) => current
      ? { ...current, conversations: current.conversations.map((c) => c.id === conversationId ? { ...c, status } : c) }
      : current
    );
  };

  return {
    conversations: data?.conversations ?? [],
    hasMore: data?.has_more ?? false,
    error,
    isLoading,
    mutate,
    createConversation,
    deleteConversation,
    updateConversationStatus,
  };
}

export function useMessages(conversationId: number | undefined) {
  const { data, error, isLoading, mutate } = useSWR(
    conversationId ? `conversations/${conversationId}/messages` : null,
    () => messagesApi.list(conversationId!, { limit: 100 }),
    { refreshInterval: 3000 },
  );

  const sendMessage = async (
    content: string,
    options?: { files?: File[]; usedTools?: string[]; customerEmail?: string },
  ) => {
    if (!conversationId) return;

    const files = options?.files ?? [];
    const optimisticId = -Date.now() - Math.floor(Math.random() * 1000);
    const queueId = `q_${optimisticId}_${Math.random().toString(36).slice(2, 8)}`;

    // Show the optimistic message IMMEDIATELY using local blob URLs for any
    // attached files. Uploads happen in the background — the user sees the
    // photo right away instead of staring at a blank screen for several seconds
    // while the upload runs. Polling will replace this with the real server
    // message + final GCS URLs once the POST completes.
    const localPreviewUrls = files.map((f) => URL.createObjectURL(f));
    const optimisticAttachments = files.length
      ? files.map((f, i) => ({
          id: -i - 1,
          storage_url: '',
          file_type: (f.type || inferContentType(f.name)).startsWith('video/') ? 'video' : 'image',
          created_at: new Date().toISOString(),
          public_url: localPreviewUrls[i],
        }))
      : null;
    const optimistic: MessageResponse = {
      id: optimisticId,
      conversation_id: conversationId,
      role: 'user',
      content,
      status: 'pending',
      attachments: optimisticAttachments,
      created_at: new Date().toISOString(),
      is_pending: true,
    };

    await mutate(
      (current) => ({
        messages: [optimistic, ...(current?.messages ?? [])],
        has_more: current?.has_more ?? false,
      }),
      { revalidate: false },
    );

    messageQueue.enqueue({
      id: queueId,
      conversationId,
      content,
      status: 'in-flight',
      optimisticMessageId: optimisticId,
      createdAt: Date.now(),
    });

    let attachments: AttachmentInput[] = [];
    try {
      if (files.length) {
        const uploads = await Promise.all(files.map((f) => storageApi.uploadToPresignedUrl(f)));
        attachments = files.map((f, i) => ({
          blob_name: uploads[i].blob_name,
          content_type: f.type || inferContentType(f.name),
        }));
      }
      const payload: MessageCreate = {
        conversation_id: conversationId,
        content: content || null,
        attachments: attachments.length ? attachments : null,
        used_tools: options?.usedTools?.length ? options.usedTools : null,
        customer_email: options?.customerEmail || null,
      };
      await messagesApi.send(payload);
    } catch (err) {
      console.error('[SEND_MESSAGE] failed', err);
      messageQueue.fail(conversationId, queueId);
      await mutate();
      // Failed: revoke immediately since the optimistic is reverted.
      localPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
      return undefined;
    }
    // Succeeded: defer revoke so polling has time to replace the optimistic
    // message with the server version (refresh interval is 3s).
    setTimeout(() => {
      localPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    }, 30_000);

    // Let refreshInterval polling pick up the real messages — avoid an
    // immediate re-fetch here which races with the backend write and can
    // briefly return stale/empty data, making messages appear to vanish.

    // Return uploaded attachment metadata so callers can chain follow-up
    // actions (e.g. JobPage maps a chat-attached signed-paper photo to the
    // currently-selected quote items via paperAuthorization).
    return {
      attachments: attachments.map((a, i) => ({
        blob_name: a.blob_name,
        content_type: a.content_type,
        is_image: (files[i]?.type || '').startsWith('image/'),
      })),
    };
  };

  // Drain the queue: each newly-arrived completed assistant message ticks the
  // oldest in-flight queue entry to done. We track the highest assistant
  // message ID we've already accounted for to avoid double-counting on poll.
  const drainedThroughRef = useRef<number>(0);
  useEffect(() => {
    if (!conversationId || !data?.messages) return;
    const assistantDone = data.messages.filter(
      (m) => m.role === 'assistant' && m.id > 0 && m.status !== 'processing',
    );
    if (!assistantDone.length) return;
    // Process in chronological order (list is newest-first).
    const sorted = [...assistantDone].sort((a, b) => a.id - b.id);
    for (const m of sorted) {
      if (m.id <= drainedThroughRef.current) continue;
      const t = m.created_at ? Date.parse(m.created_at) : Date.now();
      messageQueue.markOldestDone(conversationId, t);
      drainedThroughRef.current = m.id;
    }
  }, [conversationId, data?.messages]);

  return {
    messages: data?.messages ?? [],
    error,
    isLoading,
    mutate,
    sendMessage,
  };
}
